import { ClosableDialog } from 'components/controls/dialogs'
import { UserStockSummary } from 'lib/api'
import { getPriceFormat } from 'lib/utils'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { gameMetaData } from 'shared'

const ROW_HEIGHT = 45
const HEADER_HEIGHT = 40
const MAX_TABLE_HEIGHT = 600

interface SummaryRow {
  game: string
  uniqueArticles: number
  totalItems: number
  soldItems: number
  singlesValue?: number
  sealedValue?: number
  totalValue: number
}

interface UserStockSummaryDialogProps {
  summary: UserStockSummary | null
  onClose: () => void
}

export const UserStockSummaryDialog = ({
  summary,
  onClose,
}: UserStockSummaryDialogProps): JSX.Element => {
  const { t } = useTranslation()
  const price = (value?: number): string =>
    value === undefined ? '' : getPriceFormat(value, summary?.currency ?? 'EUR')

  // One row per game, then the totals as the last row
  const rows: SummaryRow[] = summary
    ? [
        ...summary.gameBreakdown.map((game) => ({
          ...game,
          game: gameMetaData.find((meta) => meta.idGame === game.idGame)?.name ?? `${game.idGame}`,
        })),
        {
          game: t('dashboard.total'),
          uniqueArticles: summary.totals.uniqueArticles,
          totalItems: summary.totals.totalItems,
          soldItems: summary.totals.soldItems,
          totalValue: summary.totals.stockValue,
        },
      ]
    : []

  return (
    <ClosableDialog open={!!summary} onClose={onClose} maxWidth="lg" fullWidth>
      <h3>{t('backoffice.inventorySummary')}</h3>
      {summary && summary.gameBreakdown.length === 0 ? (
        <p>{t('backoffice.inventorySummaryEmpty')}</p>
      ) : (
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table
              width={width}
              height={Math.min(rows.length * ROW_HEIGHT + HEADER_HEIGHT, MAX_TABLE_HEIGHT)}
              headerHeight={HEADER_HEIGHT}
              rowHeight={ROW_HEIGHT}
              rowCount={rows.length}
              rowGetter={({ index }) => rows[index]}
              rowClassName={({ index }) => (index === rows.length - 1 ? 'fw-bold' : '')}
            >
              <Column width={150} label={t('common.game')} dataKey="game" />
              <Column
                width={110}
                flexGrow={1}
                label={t('backoffice.uniqueArticles')}
                dataKey="uniqueArticles"
                cellRenderer={({ cellData }) => cellData.toLocaleString()}
              />
              <Column
                width={90}
                flexGrow={1}
                label={t('backoffice.items')}
                dataKey="totalItems"
                cellRenderer={({ cellData }) => cellData.toLocaleString()}
              />
              <Column
                width={90}
                flexGrow={1}
                label={t('backoffice.sold')}
                dataKey="soldItems"
                cellRenderer={({ cellData }) => cellData.toLocaleString()}
              />
              <Column
                width={120}
                flexGrow={1}
                className="text-end"
                headerRenderer={() => (
                  <div className="text-end w-100">{t('dashboard.singles')}</div>
                )}
                label={t('dashboard.singles')}
                dataKey="singlesValue"
                cellRenderer={({ cellData }) => price(cellData)}
              />
              <Column
                width={120}
                flexGrow={1}
                className="text-end"
                headerRenderer={() => <div className="text-end w-100">{t('dashboard.sealed')}</div>}
                label={t('dashboard.sealed')}
                dataKey="sealedValue"
                cellRenderer={({ cellData }) => price(cellData)}
              />
              <Column
                width={120}
                flexGrow={1}
                className="text-end"
                headerRenderer={() => <div className="text-end w-100">{t('dashboard.total')}</div>}
                label={t('dashboard.total')}
                dataKey="totalValue"
                cellRenderer={({ cellData }) => price(cellData)}
              />
            </Table>
          )}
        </AutoSizer>
      )}
    </ClosableDialog>
  )
}
